import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';

import {
  getLoanFeeCreditsAction,
  getLoanFeesViewAction,
  getLoanImpoundsAction,
  getRedesignedLoanFeesAction,
} from 'store/loanApplications/actions';
import {
  selectFormattedLoanFeesView,
  selectLoanFeesCashToCloseAmount,
} from 'store/loanApplications/selectors';
import {
  getFeeProvidersAction,
  getFeeTypeByCalculationTypeAction,
  getImpoundTypesAction,
} from 'store/system/actions';

import { getFeeToleranceBuckets } from '../../feeTolerance.utils';
import { computeFeeTotals } from '../../feeTotals.utils';

import { hasAnyFees } from './utils';

const useWizardFees = () => {
  const dispatch = useDispatch();
  const loanId = useLoanId();
  const { applicationId } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [editedSection, setEditedSection] = useState(null);

  const loanFeesView = useSelector(selectFormattedLoanFeesView);
  const cashToCloseAmount = useSelector(selectLoanFeesCashToCloseAmount);

  useEffectSafeStrictMode(() => {
    const lifecycle = { isActive: true };

    setIsLoading(true);

    Promise.all([
      dispatch(getFeeProvidersAction()),
      dispatch(getLoanFeesViewAction({ applicationId })),
      dispatch(getImpoundTypesAction({ includeDeleted: true })),
      dispatch(getLoanImpoundsAction({ applicationId })),
      dispatch(getFeeTypeByCalculationTypeAction({ calculationType: null, includeDeleted: true })),
      dispatch(getRedesignedLoanFeesAction({ loanId })),
      dispatch(getLoanFeeCreditsAction({ loanId })),
    ])
      .catch(Error)
      .finally(() => {
        if (lifecycle.isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      lifecycle.isActive = false;
    };
  }, [dispatch, applicationId, loanId]);

  const main = useMemo(() => loanFeesView?.main ?? {}, [loanFeesView]);

  const totals = useMemo(
    () => computeFeeTotals({ main, cashToCloseAmount }),
    [main, cashToCloseAmount],
  );
  const buckets = useMemo(() => getFeeToleranceBuckets(main), [main]);
  const hasFees = useMemo(() => hasAnyFees(main), [main]);

  return {
    main,
    totals,
    buckets,
    hasFees,
    isLoading,
    setIsLoading,
    editedSection,
    setEditedSection,
  };
};

export default useWizardFees;
